import React, { useState, useEffect } from 'react';
import styles from './Programs.module.scss';
import Table from '../../../components/table/Table';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import Filter from '../../../components/filter/Filter';

const Programs = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const { role } = useAuth();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    start: 1,
    end: 1,
    hasPrev: false,
    hasNext: false
  });

  const fetchPrograms = React.useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = { page, limit: 10, search };
      if (sortBy) { params.sortBy = sortBy; params.sortOrder = sortOrder; }
      if (statusFilter !== '') { params.status = statusFilter; }
      const response = await axiosClient.get(`/programs`, { params });
      const responseData = response.data;
      
      setData(responseData.programs || []);
      
      const totalPages = Math.ceil(responseData.total / responseData.limit);
      setPagination({
        current: responseData.page,
        total: responseData.total,
        start: (responseData.page - 1) * responseData.limit + 1,
        end: Math.min(responseData.page * responseData.limit, responseData.total),
        hasPrev: responseData.page > 1,
        hasNext: responseData.page < totalPages
      });
    } catch (error) {
       showToast(error.message || 'Failed to fetch programs', 'error');
    } finally {
       setIsLoading(false);
    }
  }, [showToast, search, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    fetchPrograms(1);
  }, [fetchPrograms]);

  const handlePageChange = (newPage) => {
    fetchPrograms(newPage);
  };

  const columns = [
    { 
        header: 'Program', 
        key: 'title',
        render: (row) => (
            <span 
                className={styles.programTitleLink} 
                onClick={() => navigate(`/programs/edit/${row.id}`)}
            >
                {row.title}
            </span>
        )
    },
    { header: 'Total Students', key: 'totalStudents' },
    { header: 'Total Clinics', key: 'totalClinicalSites' },
    { 
      header: 'Program Status', 
      key: 'status',
      render: (row) => {
        const statusClass = row.status === 'Active' ? styles.statusActive : 
                           row.status === 'Inactive' ? styles.statusInactive : 
                           styles.statusDefault;
        return (
          <div className={`${styles.statusBadge} ${statusClass}`}>
            <span className={styles.statusDot}></span>
            {row.status}
          </div>
        );
      }
    },
    { header: 'Program Start Date', key: 'startDate', sortKey: 'startDate', render: (row) => new Date(row.startDate).toLocaleDateString() },
    { header: 'Program End Date', key: 'endDate', sortKey: 'endDate', render: (row) => new Date(row.endDate).toLocaleDateString() },
  ];

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Program Management" />
        <div className={styles.headerActions}>
            <Search
                onSubmit={handleSearch} 
            />
            {role === 'Program Director' && (
              <Button
                label={
                    <div className={styles.cta}>
                        <Icon name="plus" />
                        Create Program
                    </div>
                }
                variant="primary"
                onClick={() => navigate('/programs/create')}
                size='medium'
              />
            )}
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={[
                { label: 'All', value: '' },
                { label: 'Active', value: true },
                { label: 'Inactive', value: false },
              ]}
              selected={statusFilter}
              onChange={(value) => setStatusFilter(value)}
            />
          </div>
        </div>
        <Table
          columns={columns}
          data={data}
          pagination={pagination}
          onPageChange={handlePageChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(key, order) => {
            setSortBy(key);
            setSortOrder(order);
          }}
        />
      </div>
    </>
  );
};

export default Programs;
